import { Box, Flex, Heading, SimpleGrid, Spinner, Text } from "@chakra-ui/react";
import { Tooltip } from "@chakra-ui/react";
import { FC } from "react";
import { useNavigate } from "react-router";

import {
  ApiUploadtasksListBroDomainEnum,
  ApiUploadtasksListStatusEnum,
  UploadTask,
} from "@nens/brostar/openapi-client";

import { KEY_ADDON_MAP, getSearchStringParam } from "@/hooks/use-router-table-data";
import { useUploadTaskListQueries } from "@/queries/query-upload-task";

const BRO_DOMAINS = ["GMW", "GLD", "FRD", "GAR", "GMN"] as const;
type BroDomain = (typeof BRO_DOMAINS)[number];

interface DomainStats {
  domain: BroDomain;
  total: number;
  completed: number;
  failed: number;
  other: number;
}

const ONE_YEAR_AGO = new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString();

const StatusBar: FC<{ completed: number; failed: number; other: number; total: number }> = ({
  completed,
  failed,
  other,
  total,
}) => {
  if (total === 0) {
    return (
      <Box
        h="8"
        bg="gray.100"
        borderRadius="md"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Text fontSize="xs" color="gray.500">
          Geen data
        </Text>
      </Box>
    );
  }

  const completedPct = (completed / total) * 100;
  const failedPct = (failed / total) * 100;
  const otherPct = (other / total) * 100;

  return (
    <Box>
      <Flex h="8" borderRadius="md" overflow="hidden" border="1px solid" borderColor="gray.200">
        {completed > 0 && (
          <Box
            bg="green.500"
            width={`${completedPct}%`}
            display="flex"
            alignItems="center"
            justifyContent="center"
            transition="all 0.3s"
          >
            {completedPct > 10 && (
              <Text fontSize="xs" fontWeight="bold" color="white">
                {completed}
              </Text>
            )}
          </Box>
        )}
        {failed > 0 && (
          <Box
            bg="red.500"
            width={`${failedPct}%`}
            display="flex"
            alignItems="center"
            justifyContent="center"
            transition="all 0.3s"
          >
            {failedPct > 10 && (
              <Text fontSize="xs" fontWeight="bold" color="white">
                {failed}
              </Text>
            )}
          </Box>
        )}
        {other > 0 && (
          <Box
            bg="gray.400"
            width={`${otherPct}%`}
            display="flex"
            alignItems="center"
            justifyContent="center"
            transition="all 0.3s"
          >
            {otherPct > 10 && (
              <Text fontSize="xs" fontWeight="bold" color="white">
                {other}
              </Text>
            )}
          </Box>
        )}
      </Flex>
      <Flex mt="2" fontSize="xs" gap="4" justifyContent="space-between">
        <Flex alignItems="center" gap="1">
          <Box w="3" h="3" bg="green.500" borderRadius="sm" />
          <Text color="gray.600">
            Voltooid: {completed} ({completedPct.toFixed(0)}%)
          </Text>
        </Flex>
        <Flex alignItems="center" gap="1">
          <Box w="3" h="3" bg="red.500" borderRadius="sm" />
          <Text color="gray.600">
            Mislukt: {failed} ({failedPct.toFixed(0)}%)
          </Text>
        </Flex>
        <Tooltip
          label="Dit bestaat uit uploadtaken die nog bezig zijn, of die de status unfinished hebben, wat betekent dat ze na 1,5 uur nog niet waren verwerkt."
          placement="top"
          hasArrow
        >
          <Flex alignItems="center" gap="1" cursor="help">
            <Box w="3" h="3" bg="gray.400" borderRadius="sm" />
            <Text color="gray.600">
              Anders: {other} ({otherPct.toFixed(0)}%)
            </Text>
          </Flex>
        </Tooltip>
      </Flex>
    </Box>
  );
};

const DomainCard: FC<{ stats: DomainStats }> = ({ stats }) => {
  const navigate = useNavigate();
  return (
    <Box
      p="5"
      borderWidth="1px"
      borderRadius="lg"
      borderColor="gray.200"
      bg="white"
      boxShadow="sm"
      _hover={{ boxShadow: "md", transform: "translateY(-2px)" }}
      transition="all 0.2s"
      cursor="pointer"
      onClick={() => {
        const searchField: keyof UploadTask = "broDomain";
        const searchString: ApiUploadtasksListBroDomainEnum = stats.domain;
        navigate(
          `/?viewType=uploadTasks&${getSearchStringParam({
            keyAddon: KEY_ADDON_MAP.UPLOAD_TASK,
            searchField,
            searchString,
          })}`
        );
      }}
    >
      <Flex justifyContent="space-between" alignItems="center" mb="3">
        <Heading as="h4" size="md" color="gray.700">
          {stats.domain}
        </Heading>
        <Text fontSize="2xl" fontWeight="bold" color="blue.600">
          {stats.total}
        </Text>
      </Flex>
      <StatusBar
        completed={stats.completed}
        failed={stats.failed}
        other={stats.other}
        total={stats.total}
      />
    </Box>
  );
};

const BroDomainStats: FC = () => {
  const queries = useUploadTaskListQueries(
    BRO_DOMAINS.flatMap((broDomain) => [
      { broDomain, status: undefined, createdGte: ONE_YEAR_AGO },
      {
        broDomain,
        status: ApiUploadtasksListStatusEnum.Completed,
        createdGte: ONE_YEAR_AGO,
      },
      { broDomain, status: ApiUploadtasksListStatusEnum.Failed, createdGte: ONE_YEAR_AGO },
    ])
  );

  const isLoading = queries.some((q) => q.isLoading);
  const isError = queries.some((q) => q.isError);

  const stats: DomainStats[] = BRO_DOMAINS.map((domain, i) => {
    const total = queries[i * 3].data?.count ?? 0;
    const completed = queries[i * 3 + 1].data?.count ?? 0;
    const failed = queries[i * 3 + 2].data?.count ?? 0;
    return { domain, total, completed, failed, other: total - completed - failed };
  });

  if (isLoading) {
    return (
      <Box textAlign="center" py="10">
        <Spinner size="xl" color="blue.500" />
        <Text mt="4" color="gray.600">
          Statistieken laden...
        </Text>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box p="5" borderWidth="1px" borderRadius="lg" borderColor="red.200" bg="red.50">
        <Text color="red.600">Kon statistieken niet laden</Text>
      </Box>
    );
  }

  const totalMessages = stats.reduce((sum, stat) => sum + stat.total, 0);

  return (
    <Box>
      <Flex justifyContent="space-between" alignItems="center" mb="5">
        <Heading as="h3" size="lg">
          BRO Leveringsoverzicht per domein (laatste 12 maanden)
        </Heading>
        <Box textAlign="right">
          <Text fontSize="sm" color="gray.600">
            Totaal berichten
          </Text>
          <Text fontSize="3xl" fontWeight="bold" color="blue.600">
            {totalMessages}
          </Text>
        </Box>
      </Flex>
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 5 }} spacing="5">
        {stats.map((stat) => (
          <DomainCard key={stat.domain} stats={stat} />
        ))}
      </SimpleGrid>
    </Box>
  );
};

export { BroDomainStats };
